<!--
  Register Component - Beautiful design matching the provided HTML
-->
<template>
  <div class="relative flex min-h-screen flex-col overflow-x-hidden gradient-bg" style='font-family: Inter, "Noto Sans", sans-serif;'>
    <div class="layout-container flex h-full grow flex-col items-center justify-center">
      <div class="w-full max-w-md bg-white dark:bg-slate-800 shadow-xl rounded-xl p-8 md:p-12 space-y-8">
        <div class="absolute top-4 right-4">
          <dark-mode-toggle />
        </div>
        <!-- Header -->
        <header class="flex flex-col items-center justify-center text-center">
          <img 
          src="@/assets/images/logo.png" 
          alt="Portinos Logo" 
          class="h-10 w-auto"
        /><br>
          <div class="flex items-center gap-3 text-[#111518] dark:text-orange-500">
            <h1 class="text-[#111518] dark:text-slate-100 tracking-tight text-3xl font-bold leading-tight pt-6 pb-2">Herramienta de Estimaciones Portinos</h1>
          </div>
          <p class="text-slate-600 dark:text-slate-400 text-base">Ingresa tus datos para crear una cuenta.</p>
        </header>

        <!-- Registration Form -->
        <form @submit.prevent="handleRegister" class="space-y-6">
          <!-- Name Field -->
          <div>
            <label class="block text-[#111518] dark:text-slate-300 text-sm font-medium leading-normal pb-1.5" for="name">Nombre Completo</label>
            <input 
              class="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-[#111518] focus:outline-none focus:ring-2 focus:ring-[#0b80ee] border border-[#dbe1e6] bg-white h-12 placeholder:text-[#60768a] p-3 text-base font-normal leading-normal" 
              id="name" 
              name="name" 
              placeholder="Ingresa tu nombre completo" 
              type="text" 
              v-model="form.name"
              :disabled="isLoading"
              required
            />
            <p v-if="errors.name" class="mt-1 text-sm text-red-600">{{ errors.name }}</p>
          </div>

          <!-- Email Field -->
          <div>
            <label class="block text-[#111518] dark:text-slate-300 text-sm font-medium leading-normal pb-1.5" for="email">Email</label>
            <input 
              class="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-[#111518] focus:outline-none focus:ring-2 focus:ring-[#0b80ee] border border-[#dbe1e6] bg-white h-12 placeholder:text-[#60768a] p-3 text-base font-normal leading-normal" 
              id="email" 
              name="email" 
              placeholder="Ingresa tu email" 
              type="email" 
              v-model="form.email"
              :disabled="isLoading"
              required
            />
            <p v-if="errors.email" class="mt-1 text-sm text-red-600">{{ errors.email }}</p>
          </div>

          <!-- Password Field -->
          <div>
            <label class="block text-[#111518] dark:text-slate-300 text-sm font-medium leading-normal pb-1.5" for="password">Contraseña</label>
            <input 
              class="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-[#111518] focus:outline-none focus:ring-2 focus:ring-[#0b80ee] border border-[#dbe1e6] bg-white h-12 placeholder:text-[#60768a] p-3 text-base font-normal leading-normal" 
              id="password" 
              name="password" 
              placeholder="Ingresa una contraseña segura" 
              type="password" 
              v-model="form.password"
              :disabled="isLoading"
              minlength="8"
              maxlength="128"
              required
            />
            <div class="mt-1">
              <p v-if="errors.password" class="text-sm text-red-600">{{ errors.password }}</p>
              <div class="text-xs text-[#60768a] dark:text-slate-400 mt-1">
                <p class="font-medium mb-1">Requisitos de contraseña:</p>
                <ul class="list-disc list-inside space-y-0.5">
                  <li>Entre 8-128 caracteres</li>
                  <li>Al menos una letra minúscula (a-z)</li>
                  <li>Al menos una letra mayúscula (A-Z)</li>
                  <li>Al menos un número (0-9)</li>
                  <li>Al menos un carácter especial (@$!%*?&)</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Confirm Password Field -->
          <div>
            <label class="block text-[#111518] dark:text-slate-300 text-sm font-medium leading-normal pb-1.5" for="confirmPassword">Confirmar Contraseña</label>
            <input 
              class="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-[#111518] focus:outline-none focus:ring-2 focus:ring-[#0b80ee] border border-[#dbe1e6] bg-white h-12 placeholder:text-[#60768a] p-3 text-base font-normal leading-normal" 
              id="confirmPassword" 
              name="confirmPassword" 
              placeholder="Confirma tu contraseña" 
              type="password" 
              v-model="form.confirmPassword"
              :disabled="isLoading"
              required
            />
            <p v-if="errors.confirmPassword" class="mt-1 text-sm text-red-600">{{ errors.confirmPassword }}</p>
          </div>

          <!-- Error Message -->
          <div v-if="authError" class="bg-red-50 border border-red-200 rounded-lg p-4">
            <div class="flex">
              <p class="text-sm text-red-700">{{ authError }}</p>
            </div>
          </div>

          <!-- Register Button -->
          <div>
            <button 
              class="flex w-full min-w-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 px-4 bg-orange-600 hover:bg-orange-700 dark:bg-orange-500 dark:hover:bg-orange-600 text-white text-base font-semibold leading-normal tracking-[0.015em] transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 dark:focus:ring-orange-400 disabled:opacity-50 disabled:cursor-not-allowed" 
              type="submit"
              :disabled="isLoading || !isFormValid"
            >
              <span v-if="isLoading" class="flex items-center">
                <div class="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                Creando cuenta...
              </span>
              <span v-else class="truncate">Crear Cuenta</span>
            </button>
          </div>

          <!-- Login Link -->
          <div class="text-center">
            <p class="text-sm text-slate-600">
              ¿Ya tienes una cuenta?
              <router-link 
                to="/login"
                class="font-medium text-orange-600 hover:text-orange-700 dark:text-orange-500 dark:hover:text-orange-400"
              >
                Inicia sesión aquí
              </router-link>
            </p>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth } from '@/composables/useAuth';
import DarkModeToggle from '@/components/ui/DarkModeToggle.vue';

export default {
  name: 'RegisterComponent',
  components: {
    DarkModeToggle
  },
  setup() {
    const router = useRouter();
    const { register, isLoading, authError, isAuthenticated } = useAuth();

    // Form state
    const form = ref({
      name: '',
      email: '',
      password: '',
      confirmPassword: ''
    });

    const errors = ref({});
    const debugMode = ref(process.env.NODE_ENV === 'development');
    const apiUrl = ref(process.env.VUE_APP_API_URL || 'http://localhost:3001');

    // Validation
    const validateForm = () => {
      errors.value = {};

      // Name validation
      if (!form.value.name) {
        errors.value.name = 'Name is required';
      } else if (form.value.name.length < 2) {
        errors.value.name = 'Name must be at least 2 characters';
      }

      // Email validation
      if (!form.value.email) {
        errors.value.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) {
        errors.value.email = 'Invalid email format';
      }

      // Password validation
      if (!form.value.password) {
        errors.value.password = 'Password is required';
      } else if (form.value.password.length < 8) {
        errors.value.password = 'Password must be at least 8 characters';
      } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(form.value.password)) {
        errors.value.password = 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character';
      }

      // Confirm password validation
      if (!form.value.confirmPassword) {
        errors.value.confirmPassword = 'Please confirm your password';
      } else if (form.value.password !== form.value.confirmPassword) {
        errors.value.confirmPassword = 'Passwords do not match';
      }

      return Object.keys(errors.value).length === 0;
    };

    const isFormValid = computed(() => {
      return form.value.name && 
             form.value.email && 
             form.value.password && 
             form.value.confirmPassword &&
             /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email) &&
             form.value.password.length >= 8 &&
             form.value.password === form.value.confirmPassword;
    });

    // Handle registration
    const handleRegister = async () => {
      console.log('🔐 Register button clicked'); // Debug log
      
      if (!validateForm()) {
        console.log('❌ Form validation failed', errors.value);
        return;
      }

      console.log('✅ Attempting registration with:', { 
        name: form.value.name, 
        email: form.value.email 
      }); // Don't log password

      try {
        const result = await register({
          name: form.value.name,
          email: form.value.email,
          password: form.value.password
        });

        console.log('📊 Registration result:', result);

        if (result.success) {
          // Redirect to intended page or dashboard
          const redirectTo = router.currentRoute.value.query.redirect || '/budgets';
          console.log('🔄 Redirecting to:', redirectTo);
          await router.push(redirectTo);
        }
      } catch (error) {
        console.error('💥 Registration error:', error);
      }
    };

    // Redirect if already authenticated
    onMounted(() => {
      console.log('🚀 RegisterComponent mounted, authenticated:', isAuthenticated.value);
      if (isAuthenticated.value) {
        router.push('/budgets');
      }
    });

    return {
      form,
      errors,
      isFormValid,
      isLoading,
      authError,
      handleRegister,
      debugMode,
      apiUrl
    };
  }
};
</script>

<style scoped>
.gradient-bg {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
}

.dark .gradient-bg {
  background: linear-gradient(135deg, theme('colors.slate.800') 0%, theme('colors.slate.900') 100%);
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
